import React, { useState } from 'react';
import { Store, Plus, X, ArrowRight, Copy, Link2 } from 'lucide-react';
import { useOrders } from '../hooks/useOrders';
import { useBusinessStore } from '../store/useBusinessStore';
import { useReadyOrders } from '../hooks/useReadyOrders';
import { supabase } from '../supabaseClient';
import { calculateSaldoPendiente } from '../utils/depositBalance';
import { orderView, orderLinesTotal, paymentLabel, isPaidOrder, orderNumber, findOrderByNumber, isPendingTransfer, paymentReminderText } from '../utils/orders';
import ManualOrderProducts from '../components/ManualOrderProducts';
import toast from 'react-hot-toast';

export default function OrdersPage() {
  const {
    orders,
    activeBusiness,
    isProcessing,
    updateOrderStatus,
    setOrderPaid,
    createManualOrder,
    formatOrderItems,
    copyForDelivery
  } = useOrders();

  const { activeBusinessId, setMasterDb } = useBusinessStore();
  const readyOrders = useReadyOrders();

  // El panel de domiciliarios es una página pública de la app (/delivery): entran con el teléfono y el PIN que tienen en Equipo.
  const copyDeliveryLink = async () => {
    const link = `${window.location.origin}/delivery`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Enlace copiado: pégalo en el WhatsApp de tus domiciliarios. Entran con su teléfono y PIN de Equipo.');
    } catch {
      window.prompt('Copia este enlace y envíaselo a tus domiciliarios:', link);
    }
  };

  // Confirmar Anticipo / Depósito
  const handleConfirmDeposit = async (orderId, depositAmount) => {
    const montoTexto = Number(depositAmount || 0).toLocaleString('es-CO');
    if (!window.confirm(`¿Confirmar que recibiste $${montoTexto} de anticipo?`)) return;

    // Guardar el estado previo del pedido para poder revertir si falla la escritura
    const previousStatus = (orders || []).find(o => String(o.id) === String(orderId))?.deposit_status;

    const applyStatus = (status) => {
      setMasterDb(prev => prev.map(b => {
        if (String(b.id) === String(activeBusinessId)) {
          return {
            ...b,
            orders: (b.orders || []).map(o => String(o.id) === String(orderId) ? { ...o, deposit_status: status } : o)
          };
        }
        return b;
      }));
    };

    applyStatus('confirmed');

    const fail = (detail) => {
      console.error('[Deposit Confirm Error]', detail);
      applyStatus(previousStatus);
      toast.error('No se pudo confirmar el anticipo. Intenta de nuevo.');
    };

    try {
      const { data, error } = await supabase
        .from('orders')
        .update({ deposit_status: 'confirmed' })
        .eq('id', orderId)
        .select('id');

      if (error) {
        fail(error.message);
        return;
      }

      // Sin error pero 0 filas actualizadas (RLS / id inexistente) => fallo silencioso
      if (!data || data.length === 0) {
        fail(`La actualización no afectó ninguna fila (${orderId})`);
        return;
      }

      toast.success('Anticipo confirmado');
    } catch (err) {
      fail(err);
    }
  };

  const [orderFilterTab, setOrderFilterTab] = useState('all');
  const [orderQuery, setOrderQuery] = useState('');

  // Pago recibido por número de pedido: sirve para cruzar un comprobante o una línea del extracto del banco con su pedido.
  const payByNumber = async () => {
    const order = findOrderByNumber(orders, orderQuery);
    if (!order) { toast.error('No encontré un pedido con ese número. Escribe solo el número, por ejemplo 12.'); return; }
    if (isPaidOrder(order)) { toast(`El pedido ${orderNumber(order)} ya figura como pagado.`, { icon: 'ℹ️' }); return; }
    const v = orderView(order);
    if (!window.confirm(`¿Registrar el pago del pedido ${orderNumber(order)} de ${v.name || 'cliente'} por $${Number(v.total || 0).toLocaleString('es-CO')}?`)) return;
    await setOrderPaid(order.id, true);
  };

  const remindPayment = (order) => {
    const phone = String(orderView(order).phone || '').replace(/\D/g, '');
    if (phone.length < 10) { toast.error('Este pedido no tiene un teléfono válido para escribirle.'); return; }
    const full = phone.length === 10 ? `57${phone}` : phone;
    window.open(`https://wa.me/${full}?text=${encodeURIComponent(paymentReminderText(order, activeBusiness))}`, '_blank');
  };
  const [showManualOrderModal, setShowManualOrderModal] = useState(false);
  const [manualOrderForm, setManualOrderForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    items_text: '',
    lines: [],
    delivery_city: '',
    payment_method: '',
    total: '0'
  });

  const handleManualOrderSubmit = (e) => {
    e.preventDefault();
    createManualOrder(manualOrderForm, () => {
      setShowManualOrderModal(false);
      setManualOrderForm({
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        items_text: '',
        lines: [],
        delivery_city: '',
        payment_method: '',
        total: '0'
      });
    });
  };

  if (!activeBusiness) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-seryi-bg-light/80 pb-4">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading">Consola de Pedidos y Órdenes</h2>
          <p className="text-xs text-seryi-text">Administración de pedidos por WhatsApp, Sitio Web o Registro Manual en local.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={copyDeliveryLink}
            title="Copia el enlace del panel de domiciliarios para enviárselo por WhatsApp"
            className="border border-seryi-bg-light text-seryi-heading hover:bg-seryi-bg-light/30 font-bold py-2.5 px-4 rounded-xl text-xs flex items-center gap-2 transition-all"
          >
            <Link2 size={14} /> Copiar enlace de domiciliarios
          </button>
          <button 
            onClick={() => setShowManualOrderModal(true)}
            className="bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-black py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 shadow-lg transition-all"
          >
            <Plus size={16} /> + Nuevo Pedido Manual
          </button>
        </div>
      </div>

      {readyOrders.hasBusiness && (
        <label className="flex items-start gap-3 p-4 rounded-2xl border border-seryi-bg-light bg-seryi-bg cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            aria-label="Los pedidos nuevos entran como Listo"
            checked={readyOrders.enabled}
            disabled={readyOrders.saving}
            onChange={(e) => readyOrders.setEnabled(e.target.checked)}
          />
          <span className="text-xs text-seryi-text">
            <b className="text-seryi-heading">Pedidos sin cocina: los pedidos nuevos entran como «Listo».</b>{' '}
            Úsalo si no preparas nada en cocina (agua, bebidas, tienda): el domiciliario ve el pedido en cuanto llega, sin que nadie lo pase a «Listo». Vale para los pedidos manuales y para los que toma el bot.
          </span>
        </label>
      )}

      {/* FILTROS DE PEDIDO */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 'all', label: 'Todos los Pedidos' },
          { id: 'whatsapp', label: '💬 WhatsApp' },
          { id: 'website', label: '🌐 Sitio Web' },
          { id: 'manual', label: '📝 Manual / Presencial' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setOrderFilterTab(tab.id)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              orderFilterTab === tab.id 
                ? 'bg-seryi-green text-seryi-bg-dark shadow-md' 
                : 'bg-seryi-bg-light/40 text-seryi-text hover:text-seryi-heading'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* MODAL NUEVO PEDIDO MANUAL */}
      {showManualOrderModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl max-w-md w-full space-y-4 shadow-2xl animate-fadeIn">
            <div className="flex items-center justify-between border-b border-seryi-bg-light pb-3">
              <h3 className="font-black text-base text-seryi-heading">Registrar Pedido Manual</h3>
              <button onClick={() => setShowManualOrderModal(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <form onSubmit={handleManualOrderSubmit} className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text">Nombre del Cliente *</label>
                <input 
                  type="text" required placeholder="Ej: Carlos Gómez"
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                  value={manualOrderForm.customer_name}
                  onChange={(e) => setManualOrderForm({...manualOrderForm, customer_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-seryi-text">Teléfono</label>
                  <input 
                    type="text" placeholder="Ej: 310..."
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                    value={manualOrderForm.customer_phone}
                    onChange={(e) => setManualOrderForm({...manualOrderForm, customer_phone: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-seryi-text">Total ($)</label>
                  <input 
                    type="number" placeholder="0"
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                    value={manualOrderForm.total}
                    onChange={(e) => setManualOrderForm({...manualOrderForm, total: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text">Dirección o Mesa</label>
                <input 
                  type="text" placeholder="Ej: Mesa 4 / Cra 12 # 34-56"
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                  value={manualOrderForm.customer_address}
                  onChange={(e) => setManualOrderForm({...manualOrderForm, customer_address: e.target.value})}
                />
              </div>
              {Array.isArray(activeBusiness.delivery_cities) && activeBusiness.delivery_cities.length > 0 && (
                <div className="space-y-1">
                  <label htmlFor="manual-city" className="text-[10px] uppercase font-bold text-seryi-text">Ciudad de entrega</label>
                  <select
                    id="manual-city"
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                    value={manualOrderForm.delivery_city}
                    onChange={(e) => setManualOrderForm({ ...manualOrderForm, delivery_city: e.target.value })}
                  >
                    <option value="">Sin ciudad (lo ven todos los domiciliarios)</option>
                    {activeBusiness.delivery_cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              <div className="space-y-1">
                <label htmlFor="manual-payment" className="text-[10px] uppercase font-bold text-seryi-text">Forma de pago</label>
                <select
                  id="manual-payment"
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                  value={manualOrderForm.payment_method}
                  onChange={(e) => setManualOrderForm({ ...manualOrderForm, payment_method: e.target.value })}
                >
                  <option value="">Sin definir</option>
                  <option value="efectivo">Efectivo (paga al recibir)</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
              <ManualOrderProducts
                lines={manualOrderForm.lines}
                onChange={(lines) => setManualOrderForm({ ...manualOrderForm, lines, total: String(orderLinesTotal(lines)) })}
              />
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text">{manualOrderForm.lines.length ? 'Notas del pedido (opcional)' : 'Detalle de Productos *'}</label>
                <textarea 
                  rows={3} required={!manualOrderForm.lines.length} placeholder="Ej: 2 Hamburguesas de Res, 1 Coca-Cola"
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                  value={manualOrderForm.items_text}
                  onChange={(e) => setManualOrderForm({...manualOrderForm, items_text: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button type="button" onClick={() => setShowManualOrderModal(false)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 rounded-xl text-xs">Cancelar</button>
                <button type="submit" disabled={isProcessing} className="flex-1 bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-black py-2.5 rounded-xl text-xs disabled:opacity-50">Guardar Pedido</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {orders && orders.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text" inputMode="search" aria-label="Buscar pedido por número o cliente" placeholder="Buscar por número o cliente (ej: 12)"
              className="flex-1 min-w-[12rem] bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={orderQuery} onChange={(e) => setOrderQuery(e.target.value)}
            />
            <button
              type="button" onClick={payByNumber} disabled={!/\d/.test(orderQuery)}
              className="px-3 py-2 rounded-xl text-xs font-bold bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/20 disabled:opacity-40"
            >
              💳 Registrar pago de este número
            </button>
          </div>
        )}
        {(!orders || orders.length === 0) ? (
          <div className="bg-seryi-bg border border-seryi-bg-light p-8 rounded-3xl text-center space-y-2">
            <Store className="mx-auto text-slate-600" size={32} />
            <p className="text-sm font-bold text-seryi-heading">No hay pedidos registrados todavía.</p>
            <p className="text-xs text-seryi-text max-w-sm mx-auto">Cuando los clientes pidan por WhatsApp, Sitio Web o de forma Manual, aparecerán aquí.</p>
          </div>
        ) : (
          [...orders]
            .filter(order => {
              if (orderFilterTab === 'all') return true;
              if (orderFilterTab === 'whatsapp') return order.source === 'whatsapp' || !order.source;
              if (orderFilterTab === 'website') return order.source === 'web' || order.source === 'website';
              if (orderFilterTab === 'manual') return order.source === 'manual';
              return true;
            })
            .filter(order => {
              const q = orderQuery.trim().toLowerCase();
              if (!q) return true;
              const digits = q.replace(/\D/g, '');
              return (digits && String(order.order_number || '') === digits) || orderView(order).name.toLowerCase().includes(q) || orderNumber(order).toLowerCase() === q;
            })
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map(order => {
              const statusKey = order.status || 'pending';
              const isPending = statusKey === 'pending';
              const isInPrep = statusKey === 'in_preparation';
              const isShipped = statusKey === 'shipped';
              const isDelivered = statusKey === 'delivered';
              const isCancelled = statusKey === 'cancelled';
              const isCancelledByClient = statusKey === 'cancelled_by_client';

              const itemsText = formatOrderItems(order);
              const view = orderView(order);

              return (
                <div 
                  key={order.id} 
                  className={`bg-seryi-bg border ${isCancelled || isCancelledByClient ? 'border-rose-900/40 opacity-70' : isDelivered ? 'border-emerald-800/40' : isShipped ? 'border-purple-800/40' : isInPrep ? 'border-blue-800/40' : 'border-amber-800/40'} p-6 rounded-3xl space-y-4 shadow-lg transition-all`}
                >
                  {/* Cabecera del Pedido */}
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-seryi-bg-light/80 pb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-seryi-bg-light/50 text-seryi-heading px-2 py-0.5 rounded text-[10px] font-mono tracking-wider">{orderNumber(order)}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDelivered ? 'bg-emerald-500/10 text-emerald-400' : isShipped ? 'bg-purple-500/10 text-purple-400' : isInPrep ? 'bg-blue-500/10 text-blue-400' : isCancelled || isCancelledByClient ? 'bg-rose-500/10 text-rose-400' : 'bg-amber-500/10 text-amber-400'}`}>
                          {isPending ? '⏳ Pendiente' : isInPrep ? '🍳 En Preparación' : isShipped ? '🛵 Despachado' : isDelivered ? '✅ Entregado' : '❌ Cancelado'}
                        </span>
                      </div>
                      <h3 className="font-bold text-sm text-seryi-heading">{view.name || 'Sin Nombre'}</h3>
                      <p className="text-xs text-seryi-text">{view.phone || 'N/A'}</p>
                      {Number(order.payment_reminders_sent) > 0 && !isPaidOrder(order) && <p className="text-[11px] text-amber-300 mt-0.5">🔔 Recordatorio de pago enviado ({order.payment_reminders_sent})</p>}
                      <p className="text-[11px] font-bold text-seryi-heading mt-0.5">💳 {paymentLabel(view.payment)} · {isPaidOrder(order) ? '✅ Pagado' : '⏳ Por cobrar'}</p>
                      {order.delivery_note && (isCancelled || isCancelledByClient || !isPaidOrder(order)) && <p className="text-[11px] text-rose-300 mt-0.5">📝 Domiciliario: {order.delivery_note}</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-black text-lg text-seryi-green">${view.total.toLocaleString('es-CO')}</p>
                      <p className="text-[10px] text-seryi-text">{new Date(order.created_at).toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Cuerpo del Pedido */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <span className="text-[10px] uppercase font-bold text-slate-400">Dirección / Mesa:</span>
                      <p className="text-sm font-semibold text-slate-200 bg-seryi-bg-dark p-2.5 rounded-xl border border-seryi-bg-light/50">{view.address || 'Presencial / Mostrador'}{view.city ? ` · ${view.city}` : ''}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] uppercase font-bold text-slate-400">Detalle del Pedido:</span>
                      <div className="text-xs text-slate-300 bg-seryi-bg-dark p-2.5 rounded-xl border border-seryi-bg-light/50 whitespace-pre-line font-mono leading-relaxed">
                        {itemsText}
                      </div>
                    </div>
                  </div>

                  {/* Anticipo / Saldo Pendiente */}
                  {order.deposit_amount > 0 && (
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-seryi-text">
                        Anticipo: <span className="font-bold text-seryi-heading">${Number(order.deposit_amount).toLocaleString('es-CO')}</span>
                        {order.deposit_status === 'declared' && (
                          <span className="ml-1.5 text-amber-400">⚠️ Declarado</span>
                        )}
                        {order.deposit_status === 'confirmed' && (
                          <span className="ml-1.5 text-emerald-400">✅ Confirmado</span>
                        )}
                      </span>
                      {order.deposit_status === 'declared' && (
                        <button
                          onClick={() => handleConfirmDeposit(order.id, order.deposit_amount)}
                          className="text-[10px] bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-bold px-2 py-0.5 rounded-lg"
                        >
                          Confirmar
                        </button>
                      )}
                    </div>
                  )}
                  {order.deposit_amount > 0 && (
                    <div className="text-[11px] text-seryi-text mt-0.5">
                      Saldo pendiente: <span className="font-bold text-seryi-heading">
                        ${calculateSaldoPendiente(view.total, order.deposit_amount, order.deposit_status).toLocaleString('es-CO')}
                      </span>
                    </div>
                  )}

                  {/* Acciones Rápidas */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-seryi-bg-light/80 pt-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] uppercase font-bold text-seryi-text mr-1">Cambiar Estado:</span>
                      
                      <button
                        onClick={() => updateOrderStatus(order.id, 'in_preparation')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isInPrep ? 'bg-blue-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading'}`}
                      >
                        En Preparación
                      </button>
                      
                      <button
                        onClick={() => updateOrderStatus(order.id, 'shipped')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isShipped ? 'bg-purple-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading'}`}
                      >
                        Despachado
                      </button>

                      <button
                        onClick={() => setOrderPaid(order.id, !view.paid)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${view.paid ? 'bg-emerald-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-emerald-900/30 text-emerald-300'}`}
                      >
                        {view.paid ? '✅ Pago recibido' : '💳 Registrar pago recibido'}
                      </button>

                      <button
                        onClick={() => updateOrderStatus(order.id, 'delivered', { is_paid: true })}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isPaidOrder(order) ? 'bg-emerald-600 text-seryi-heading shadow-lg' : 'bg-seryi-bg-light hover:bg-emerald-900/30 text-emerald-300'}`}
                      >
                        {isPaidOrder(order) ? '✅ Entregado y Pagado' : '💰 Marcar como Pagado'}
                      </button>

                      <button
                        onClick={() => updateOrderStatus(order.id, 'cancelled_by_client')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isCancelledByClient ? 'bg-rose-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-rose-900/30 text-rose-300'}`}
                      >
                        Usuario Canceló
                      </button>
                      
                      <button
                        onClick={() => updateOrderStatus(order.id, 'cancelled')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isCancelled ? 'bg-rose-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-rose-900/30 text-rose-300'}`}
                      >
                        Rechazar Pedido
                      </button>
                    </div>

                    {isPendingTransfer(order) && (
                      <button
                        onClick={() => remindPayment(order)}
                        className="px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/20 flex items-center gap-1.5 transition-all"
                      >
                        🔔 Recordar pago por WhatsApp
                      </button>
                    )}
                    <button
                      onClick={() => copyForDelivery(order)}
                      className="px-3 py-1.5 rounded-xl text-xs font-bold bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 border border-indigo-500/20 flex items-center gap-1.5 transition-all"
                    >
                      <Copy size={12} /> Copiar para Domiciliario
                    </button>
                  </div>
                </div>
              );
            })
        )}
      </div>
    </div>
  );
}
